import {
	getRegEdRevisesApprovalRequestId,
	IRegEdSubmissionInput,
	IRegEdSubmissionView,
	isRegEdDecisionCode,
	isRegEdNotSubmitted,
	isRegEdResendableFailure,
	REGED_CONDITIONALLY_APPROVED_DECISION_CODE,
	REGED_DECLINED_DECISION_CODE,
	REGED_REVISABLE_DECISION_CODE,
	RegEdComplianceContentType,
	RegEdSubmissionStatus,
	useAccountUsesRegEd,
	useRegEdAnnotationDownloadMutation,
	useRegEdSubmissionsQuery,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { CampaignType } from '../../../../models/AdminModels';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { Pill } from '../../../components-v2/Pill';
import { ThemeColors } from '../../../components-v2/Theme/theme';
import { RegEdComplianceApprovalPrompt } from '../../campaigns/ComplianceApprovalPrompt/RegEdComplianceApprovalPrompt';
import { getRegEdFailureText } from '../regEdFailureText';
import { styleSheet } from './styles';

interface IProps {
	parentId?: string;
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * Renders "Revise & Resubmit" on a revisable rejection, and "Edit & Resend" on a filing RegEd never
	 * accepted — the caller files the latter afresh (see `getRegEdRevisesApprovalRequestId`).
	 */
	onRevise?: (submission: IRegEdSubmissionView) => void;
	/**
	 * Files the answers for a scheduled piece whose own submission was refused — the same approval request,
	 * then the compliance document. Without it the panel explains the hold but offers no way to finish it.
	 */
	onFinishSubmission?: (submission: IRegEdSubmissionView & { metadata: IRegEdSubmissionInput }) => Promise<unknown>;
	isFinishing?: boolean;
	/** The content's owner, whose RegEd mapping the finished filing is checked against. */
	ownerUserId?: string | null;
	/** Card chrome belongs to the host — this panel sits in three surfaces with different idioms. */
	styles?: StyleDeclarationValue[];
	/**
	 * Authoritative held signal from the parent's approval responses (a release clears it); the
	 * submissions endpoint doesn't carry it, so without it held is inferred from the decision code.
	 */
	conditionsPending?: boolean;
	onReleaseConditions?: (submission: IRegEdSubmissionView) => void;
	isReleasing?: boolean;
	/** Email revise is a multi-call sequence, so the trigger has to hold until it settles. */
	isRevising?: boolean;
	/**
	 * Holds the action without claiming a revision is in flight — a filing that failed leaves the round
	 * reopened, and starting a second revision would supersede it.
	 */
	reviseDisabled?: boolean;
	onSendWithoutCompliance?: () => void;
	sendWithoutComplianceLabel?: string;
	disabled?: boolean;
	sentWithoutWaiting?: boolean;
	sentWithoutWaitingBy?: string;
}

type PillColor = keyof ThemeColors['pill'];

const getStatusPresentation = (status?: RegEdSubmissionStatus): { label: string; color: PillColor } => {
	switch (status) {
		case RegEdSubmissionStatus.Approved:
			return { label: 'Approved', color: 'green' };
		case RegEdSubmissionStatus.Rejected:
			return { label: 'Rejected', color: 'red' };
		case RegEdSubmissionStatus.Pending:
		case RegEdSubmissionStatus.Submitting:
			return { label: 'In review', color: 'yellow' };
		case RegEdSubmissionStatus.Withdrawn:
			return { label: 'Withdrawn', color: 'gray' };
		case RegEdSubmissionStatus.Failed:
			return { label: 'Submission failed', color: 'red' };
		case RegEdSubmissionStatus.Expired:
			return { label: 'Review expired', color: 'orange' };
		default:
			return { label: 'Status unknown', color: 'orange' };
	}
};

const CAMPAIGN_TYPE_BY_CONTENT_TYPE: Partial<Record<RegEdComplianceContentType, CampaignType>> = {
	[RegEdComplianceContentType.Email]: CampaignType.Email,
	[RegEdComplianceContentType.Blog]: CampaignType.Blog,
	[RegEdComplianceContentType.SocialMediaPost]: CampaignType.Social,
};

const getAttentionText = (submission: IRegEdSubmissionView): string | null => {
	switch (submission.status) {
		case RegEdSubmissionStatus.Failed:
			return (
				getRegEdFailureText(submission.failureReason) ??
				'The submission to your compliance system failed. Please contact support.'
			);
		case RegEdSubmissionStatus.Unknown:
			return "We couldn't confirm this submission reached your compliance system. Contact support if the status doesn't update.";
		case RegEdSubmissionStatus.Expired:
			return 'The compliance review aged out without a decision. Follow up with your reviewer.';
		default:
			return null;
	}
};

/**
 * Replaces the internal approve/reject actions on RegEd accounts — AdTrax owns the decision; this
 * surfaces state plus the delegated advisor actions (revise, conditional-hold release). Renders
 * nothing unless the account uses RegEd and the parent has a submission.
 */
export const RegEdComplianceStatus: React.FC<IProps> = ({
	parentId,
	impersonationContext,
	onRevise,
	onFinishSubmission,
	isFinishing,
	ownerUserId,
	conditionsPending,
	onReleaseConditions,
	isReleasing,
	isRevising,
	reviseDisabled,
	onSendWithoutCompliance,
	sendWithoutComplianceLabel,
	disabled,
	sentWithoutWaiting,
	sentWithoutWaitingBy,
	styles = [],
}) => {
	const usesRegEd = useAccountUsesRegEd(impersonationContext);
	const {
		data: submissions,
		isLoading,
		isSuccess,
	} = useRegEdSubmissionsQuery({
		parentId,
		impersonationContext,
		enabled: usesRegEd,
	});
	// Releasing publishes: a held campaign with no start date goes out immediately. Confirmed here
	// rather than in each host so email, social and blog all get the same speed bump.
	const [pendingRelease, setPendingRelease] = React.useState<IRegEdSubmissionView | null>(null);
	const [finishing, setFinishing] = React.useState<IRegEdSubmissionView | null>(null);
	const annotationDownload = useRegEdAnnotationDownloadMutation({
		onError: () => toast.error("Couldn't download the reviewer's notes. Please try again."),
	});

	if (!usesRegEd) {
		return null;
	}

	if (isLoading) {
		return (
			<div className={css(styleSheet.card, ...styles)}>
				<div className={css(styleSheet.title)}>Compliance Review</div>
				<div className={css(styleSheet.detail)}>Checking compliance status…</div>
			</div>
		);
	}

	// Newest-first — the first row is the latest submission chain.
	const latest = submissions?.[0];
	if (!latest) {
		// Only on a resolved empty result: an errored query also lands here with no data, and would
		// then claim nothing was filed for content that is under review.
		return onSendWithoutCompliance && isSuccess ? (
			<div className={css(styleSheet.card, ...styles)}>
				<div className={css(styleSheet.title)}>Compliance Review</div>
				<div className={css(styleSheet.explanation)}>
					Nothing was filed with your compliance system for this one. You can send it without a review.
				</div>
				<Button variant='secondary' size='sm' styles={[styleSheet.actionButton]} onClick={onSendWithoutCompliance}>
					{sendWithoutComplianceLabel ?? 'Send Anyway'}
				</Button>
			</div>
		) : null;
	}

	const conditionallyApproved = isRegEdDecisionCode(latest.decisionCode, REGED_CONDITIONALLY_APPROVED_DECISION_CODE);
	const held = conditionallyApproved && conditionsPending !== false;
	const released = conditionallyApproved && conditionsPending === false;
	const declined =
		latest.status === RegEdSubmissionStatus.Rejected &&
		isRegEdDecisionCode(latest.decisionCode, REGED_DECLINED_DECISION_CODE);
	const revisionRequested =
		latest.status === RegEdSubmissionStatus.Rejected &&
		isRegEdDecisionCode(latest.decisionCode, REGED_REVISABLE_DECISION_CODE);
	const resendableFailure = isRegEdResendableFailure(latest);
	const notSubmitted = isRegEdNotSubmitted(latest);
	const reviewNotes = (latest.reviewNotes ?? [])
		.map(note => note.noteText?.trim())
		.filter((noteText): noteText is string => !!noteText);

	// The parent's status does not move when a decision lands, so only this panel can tell a live
	// filing from a decided one. Keyed on the decision, not on liveness — the stuck states (failed,
	// expired, unknown, withdrawn) keep the bypass, since it is their only way out. A failure that came
	// back with a reason has had its approval request rejected, so resending is its way out instead, and
	// a placeholder's is finishing its questions.
	const reviewerDecided =
		resendableFailure ||
		(notSubmitted && !!onFinishSubmission) ||
		held ||
		released ||
		declined ||
		revisionRequested ||
		latest.status === RegEdSubmissionStatus.Approved ||
		latest.status === RegEdSubmissionStatus.Rejected;

	const presentation: { label: string; color: PillColor } = held
		? { label: 'Conditionally approved — held', color: 'orange' }
		: declined
			? { label: 'Declined', color: 'red' }
			: resendableFailure
				? { label: 'Not filed', color: 'red' }
				: notSubmitted
					? { label: 'Compliance questions needed', color: 'orange' }
					: getStatusPresentation(latest.status);
	const attentionText = getAttentionText(latest);
	// A refused retry updates the placeholder in place, so its last-modified date is the latest attempt.
	const metaDate = notSubmitted ? (latest.lastModifiedDate ?? latest.creationDate) : latest.creationDate;
	const annotations = (latest.annotations ?? []).filter(a => !!a.id);

	const handleDownloadAnnotation = (annotation: (typeof annotations)[number]) => {
		if (annotationDownload.isPending || !parentId || !latest.approvalRequestId) {
			return;
		}
		annotationDownload.mutate({
			parentId,
			approvalRequestId: latest.approvalRequestId,
			annotation,
			impersonationContext,
		});
	};

	return (
		<div className={css(styleSheet.card, ...styles)}>
			<div className={css(styleSheet.title)}>Compliance Review</div>
			<Pill variant='label' color={presentation.color} label={presentation.label} />
			{metaDate ? (
				<div className={css(styleSheet.metaRow)}>
					<span className={css(styleSheet.metaLabel)}>{notSubmitted ? 'Attempted' : 'Submitted'}</span>
					<span className={css(styleSheet.metaValue)}>{getDefaultDateStringValue(metaDate)}</span>
				</div>
			) : null}
			{latest.trackingNumber ? (
				<div className={css(styleSheet.metaRow)}>
					<span className={css(styleSheet.metaLabel)}>Tracking #</span>
					<span className={css(styleSheet.metaValue)}>{latest.trackingNumber}</span>
				</div>
			) : null}
			{/* An audit artifact: without it a rushed filing is indistinguishable from one that met the window. */}
			{latest.reviewLeadTimeOverride ? (
				<div className={css(styleSheet.detail)}>
					Requested on short notice
					{latest.reviewLeadTimeOverride.requestedReviewDate
						? ` for ${getDefaultDateStringValue(latest.reviewLeadTimeOverride.requestedReviewDate)}`
						: ''}
					{latest.reviewLeadTimeOverride.reason ? `: ${latest.reviewLeadTimeOverride.reason}` : ''}
				</div>
			) : null}
			{held ? (
				<>
					<div className={css(styleSheet.explanation)}>
						Your reviewer approved this with conditions. It stays held — and won't send, even past its scheduled date —
						until you complete the conditions and release it.
					</div>
					{reviewNotes.length > 0 ? (
						<>
							<div className={css(styleSheet.detail)}>Conditions to complete:</div>
							{reviewNotes.map((noteText, i) => (
								<div key={i} className={css(styleSheet.explanation)}>
									{noteText}
								</div>
							))}
						</>
					) : null}
				</>
			) : null}
			{released ? <div className={css(styleSheet.explanation)}>Conditions completed — released to send.</div> : null}
			{declined ? (
				<div className={css(styleSheet.explanation)}>
					Your reviewer declined this. It can't be revised or resubmitted.
				</div>
			) : null}
			{revisionRequested ? (
				reviewNotes.length > 0 ? (
					<>
						<div className={css(styleSheet.detail)}>Your reviewer requested revisions:</div>
						{reviewNotes.map((noteText, i) => (
							<div key={i} className={css(styleSheet.explanation)}>
								{noteText}
							</div>
						))}
					</>
				) : (
					<div className={css(styleSheet.explanation)}>
						Your reviewer requested revisions before this can be approved.
					</div>
				)
			) : !held && !released && latest.status === RegEdSubmissionStatus.Rejected ? (
				<>
					{/* The reason matters most on a rejection that can't be revised — it's the only thing the
					    advisor gets to learn from, since the piece is terminal. */}
					{reviewNotes.map((noteText, i) => (
						<div key={i} className={css(styleSheet.explanation)}>
							Your reviewer's reason: {noteText}
						</div>
					))}
					{latest.decisionCode ? (
						<div className={css(styleSheet.metaRow)}>
							<span className={css(styleSheet.metaLabel)}>Decision</span>
							<span className={css(styleSheet.metaValue)}>{latest.decisionCode}</span>
						</div>
					) : null}
				</>
			) : null}
			{sentWithoutWaiting ? (
				<div className={css(styleSheet.explanation)}>
					{sentWithoutWaitingBy
						? `Sent by ${sentWithoutWaitingBy} without waiting for this review.`
						: 'Sent without waiting for this review.'}{' '}
					The filing stays open with your firm, and a decision on it will not change what went out.
				</div>
			) : null}
			{attentionText ? <div className={css(styleSheet.explanation)}>{attentionText}</div> : null}
			{annotations.length > 0 ? (
				<div className={css(styleSheet.annotations)}>
					<div className={css(styleSheet.detail)}>Annotated documents:</div>
					{annotations.map(annotation => (
						<Button
							key={annotation.id}
							variant='link'
							size='sm'
							styles={[styleSheet.annotationLink]}
							disabled={annotationDownload.isPending}
							onClick={() => handleDownloadAnnotation(annotation)}
						>
							{annotation.fileName || 'Reviewer annotation'}
						</Button>
					))}
				</div>
			) : null}
			{held && onReleaseConditions ? (
				<Button
					variant='primary'
					size='sm'
					styles={[styleSheet.actionButton]}
					isLoading={isReleasing}
					disabled={isReleasing}
					onClick={() => setPendingRelease(latest)}
				>
					Release &amp; send
				</Button>
			) : null}
			<Dialog open={!!pendingRelease} onOpenChange={open => (open ? null : setPendingRelease(null))}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Release this for sending?</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.detail)}>
							Your reviewer approved this with conditions, and you're confirming they're met. It sends on its scheduled
							date — or right away if that date has passed.
						</div>
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={() => setPendingRelease(null)}>
							Cancel
						</Button>
						<Button
							variant='primary'
							isLoading={isReleasing}
							disabled={isReleasing}
							onClick={() => {
								const submission = pendingRelease;
								setPendingRelease(null);
								if (submission) {
									onReleaseConditions?.(submission);
								}
							}}
						>
							Release &amp; send
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			{revisionRequested && onRevise ? (
				<Button
					variant='secondary'
					size='sm'
					styles={[styleSheet.actionButton]}
					disabled={isRevising || reviseDisabled}
					isLoading={isRevising}
					onClick={() => onRevise(latest)}
				>
					Revise & Resubmit
				</Button>
			) : null}
			{resendableFailure && onRevise ? (
				<Button
					variant='secondary'
					size='sm'
					styles={[styleSheet.actionButton]}
					disabled={isRevising || reviseDisabled}
					isLoading={isRevising}
					onClick={() => onRevise(latest)}
				>
					Edit & Resend
				</Button>
			) : null}
			{notSubmitted && !onFinishSubmission ? (
				<div className={css(styleSheet.explanation)}>Open it in its editor to finish them.</div>
			) : null}
			{notSubmitted && onFinishSubmission ? (
				<Button
					variant='primary'
					size='sm'
					styles={[styleSheet.actionButton]}
					// A filing that landed but whose document failed is owed a retry, and re-posting it would replay the filing.
					disabled={isFinishing || disabled || reviseDisabled}
					isLoading={isFinishing}
					onClick={() => setFinishing(latest)}
				>
					Finish compliance questions
				</Button>
			) : null}
			<Dialog open={!!finishing} onOpenChange={open => (open || isFinishing ? null : setFinishing(null))}>
				<DialogPopup showCloseButton={!isFinishing}>
					<DialogHeader>
						<DialogTitle>Finish the compliance questions</DialogTitle>
						<DialogDescription>
							This is scheduled and held until it's filed. Answer your firm's questionnaire to send it for review.
						</DialogDescription>
					</DialogHeader>
					{finishing ? (
						// Starts blank on purpose: the API records the placeholder before any answers were accepted, so it carries none.
						<RegEdComplianceApprovalPrompt
							campaignType={finishing.contentType ? CAMPAIGN_TYPE_BY_CONTENT_TYPE[finishing.contentType] : undefined}
							// Filed as a revision when it revises a round. The send date isn't known here, so first use stays with the advisor.
							isRevision={!!finishing.revisesApprovalRequestId}
							revisionSendDate={null}
							impersonationContext={impersonationContext}
							revisesApprovalRequestId={getRegEdRevisesApprovalRequestId(finishing)}
							ownerUserId={ownerUserId}
							isLoading={isFinishing}
							submitLabel='Send for Review'
							styles={[styleSheet.finishForm]}
							onScheduleRegEdCompliance={async (_, metadata) => {
								try {
									await onFinishSubmission?.({ ...finishing, metadata });
								} finally {
									// Failures surface as a toast or the host's retry card, not by holding the dialog open.
									setFinishing(null);
								}
							}}
						/>
					) : null}
				</DialogPopup>
			</Dialog>
			{onSendWithoutCompliance && !reviewerDecided ? (
				<Button
					variant='secondary'
					size='sm'
					disabled={disabled}
					styles={[styleSheet.actionButton]}
					onClick={onSendWithoutCompliance}
				>
					{sendWithoutComplianceLabel ?? 'Send Anyway'}
				</Button>
			) : null}
		</div>
	);
};
